import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useFinanceStore } from "../stores/finance-store";
import { toast } from "sonner";
import { addDays, addMonths, addWeeks, addYears } from "date-fns";
import { shiftDateByDelta } from "../lib/finance-calculations";
import { validateCategoryParent } from "../lib/category-tree";
import { findAccountByIdOrName, getAccountDisplayName, getTransferAccountIds } from "../lib/account-relations";
import { findCategoryById, findUniqueCategoryByName, getTransactionCategory } from "../lib/category-relations";
import { moneyToUnits, normalizeCategoryName, normalizeUppercase, parseCurrency, roundMoney, stringToColor } from "../lib/utils";
import { getCategoryTypeForTransactionKind, getTransactionKind, getTransferAccounts, TRANSACTION_KINDS } from "../lib/transaction-types";

const transactionSchema = z.object({
  description: z.string().min(1, "Descrição é obrigatória"),
  amount: z.string().min(1, "Valor é obrigatório"),
  category: z.string().optional(),
  account: z.string().optional(),
  date: z.date(),
  notes: z.string().max(400, "Observação deve ter no máximo 400 caracteres").optional(),
});

const DEFAULT_REPEAT_SETTINGS = {
  recurrenceFrequency: "monthly",
  recurrenceInterval: "1",
  installmentCount: "2",
  installmentStartIndex: "1",
  amountMode: "per_installment",
  fixedTotalEnabled: false,
  fixedTotalCount: "12",
};

function makeDetailId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `detail-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createDetailItem(detail = {}) {
  return {
    id: detail.id || makeDetailId(),
    description: normalizeUppercase(detail.description),
    amount: detail.amount != null ? String(detail.amount) : "",
  };
}

function toPositiveInt(value, fallback, min = 1) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(min, parsed);
}

function toUnits(value) {
  return moneyToUnits(value);
}

function addByFrequency(date, frequency, amount) {
  if (frequency === "daily") return addDays(date, amount);
  if (frequency === "weekly") return addWeeks(date, amount);
  if (frequency === "yearly") return addYears(date, amount);
  return addMonths(date, amount);
}

function isDateOnOrBeforeToday(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return localDate <= localToday;
}

function calculateOccurrencesUntilDecember(startDate, frequency, interval) {
  const endDate = new Date(startDate.getFullYear(), 11, 31, 23, 59, 59, 999);
  let count = 0;
  let nextDate = startDate;

  while (nextDate <= endDate && count < 366) {
    count += 1;
    nextDate = addByFrequency(startDate, frequency, count * interval);
  }

  return Math.max(1, count);
}

function stripRecurrenceData(transaction) {
  const {
    recurrence_id,
    recurrence_type,
    recurrence_frequency,
    recurrence_interval,
    installment_index,
    total_installments,
    amount_mode,
    installment_start_index,
    fixed_total_occurrences,
    ...rest
  } = transaction || {};
  return rest;
}

function stripKindData(transaction) {
  const {
    transaction_kind,
    details,
    notes,
    confirmed,
    reconciled,
    linked_transfer_id,
    transfer_role,
    from_account,
    to_account,
    from_account_id,
    to_account_id,
    account_id,
    transfer_description,
    ...rest
  } = transaction || {};
  return rest;
}

function storageTypeForKind(kind) {
  return kind === TRANSACTION_KINDS.INCOME ? "income" : "expense";
}

function categoryTypeForKind(kind) {
  return getCategoryTypeForTransactionKind(kind);
}

function cleanInstallmentSuffix(description = "") {
  return description.replace(/\s*\(\d+\/\d+\)$/, "");
}

function normalizeDetails(details) {
  return Array.isArray(details) ? details.map(createDetailItem) : [];
}

function getFormDescription(transaction, kind) {
  if (kind === TRANSACTION_KINDS.TRANSFER) {
    return normalizeUppercase(transaction?.transfer_description || "Transferência");
  }
  return normalizeUppercase(cleanInstallmentSuffix(transaction?.description || ""));
}

function appendInstallmentSuffix(description, source = {}) {
  const installmentIndex = source.installment_index || source.installmentIndex;
  const totalInstallments = source.total_installments || source.totalInstallments;
  const recurrenceType = source.recurrence_type || source.recurrenceData?.recurrence_type;

  if (installmentIndex && totalInstallments && recurrenceType === "installment") {
    return `${description} (${installmentIndex}/${totalInstallments})`;
  }

  return description;
}

function validateDetailedItems(items, targetAmount) {
  const targetUnits = moneyToUnits(targetAmount);
  const filled = items.filter((item) => {
    return item.description?.trim() || toUnits(item.amount) > 0;
  });

  if (!filled.length) {
    return { success: false, message: "Inclua ao menos um detalhe." };
  }

  const invalidDescription = filled.some((item) => !item.description?.trim());
  if (invalidDescription) {
    return { success: false, message: "Todos os detalhes precisam de descrição." };
  }

  const invalidAmount = filled.some((item) => toUnits(item.amount) <= 0);
  if (invalidAmount) {
    return { success: false, message: "Todos os detalhes precisam de valor maior que zero." };
  }

  const detailsUnits = filled.reduce((sum, item) => sum + toUnits(item.amount), 0);
  if (detailsUnits !== targetUnits) {
    return { success: false, message: "A soma dos detalhes deve ser exatamente igual ao Valor Total." };
  }

  return {
    success: true,
    details: filled.map((item) => ({
      description: normalizeUppercase(item.description),
      amount: roundMoney(parseCurrency(item.amount)),
    })),
  };
}

function buildTransferOccurrenceTransactions(occurrence, context) {
  const { baseDescription, originAccount, targetAccount, origin, target, categoryFields, confirmed } = context;
  const transferId = crypto.randomUUID();

  return [
    {
      type: "expense",
      data: {
        description: appendInstallmentSuffix(`${baseDescription} PARA ${targetAccount}`, occurrence),
        amount: occurrence.amount,
        ...categoryFields,
        account_id: origin?.id || null,
        account: originAccount,
        date: occurrence.date.toISOString(),
        status: confirmed,
        confirmed,
        transaction_kind: TRANSACTION_KINDS.TRANSFER,
        linked_transfer_id: transferId,
        transfer_role: "origin",
        from_account: originAccount,
        to_account: targetAccount,
        from_account_id: origin?.id || null,
        to_account_id: target?.id || null,
        transfer_description: baseDescription,
        ...occurrence.recurrenceData,
      },
    },
    {
      type: "income",
      data: {
        description: appendInstallmentSuffix(`${baseDescription} DE ${originAccount}`, occurrence),
        amount: occurrence.amount,
        ...categoryFields,
        account_id: target?.id || null,
        account: targetAccount,
        date: occurrence.date.toISOString(),
        status: confirmed,
        confirmed,
        transaction_kind: TRANSACTION_KINDS.TRANSFER,
        linked_transfer_id: transferId,
        transfer_role: "destination",
        from_account: originAccount,
        to_account: targetAccount,
        from_account_id: origin?.id || null,
        to_account_id: target?.id || null,
        transfer_description: baseDescription,
        ...occurrence.recurrenceData,
      },
    },
  ];
}

function findLinkedTransfer(transactions, transaction) {
  if (!transaction?.linked_transfer_id) return null;
  return transactions.find((item) => (
    item.linked_transfer_id === transaction.linked_transfer_id && item.id !== transaction.id
  )) || null;
}

function getEditableTransferAccounts(transactions, transaction, accounts = []) {
  const linkedTransaction = findLinkedTransfer(transactions, transaction);
  const fallback = getTransferAccounts(transaction, linkedTransaction);
  const { fromAccountId, toAccountId } = getTransferAccountIds(transaction, linkedTransaction, accounts);
  const fromAccount = findAccountByIdOrName(accounts, fromAccountId, fallback.fromAccount);
  const toAccount = findAccountByIdOrName(accounts, toAccountId, fallback.toAccount);
  return {
    fromAccount: fromAccount?.name || fallback.fromAccount,
    toAccount: toAccount?.name || fallback.toAccount,
  };
}

function uniqueNames(values) {
  const seen = new Set();
  return values
    .map((value) => normalizeUppercase(value))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLocaleLowerCase("pt-BR");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export const useTransactionForm = (open, onOpenChange, editData, initialData = null) => {
  const initialSource = editData || initialData || {};
  const initialKind = getTransactionKind(initialSource);
  const initialAccountName = editData?.account || initialData?.account || initialData?.from_account || "";
  const [transactionKind, setTransactionKindState] = useState(initialKind);
  const [type, setType] = useState(categoryTypeForKind(initialKind));
  const [categoryValue, setCategoryValue] = useState(normalizeCategoryName(initialSource?.category));
  const [categoryIdValue, setCategoryIdValue] = useState(initialSource?.category_id || null);
  const [accountValue, setAccountValue] = useState(initialAccountName);
  const [destinationAccountValue, setDestinationAccountValue] = useState(editData?.to_account || initialData?.to_account || "");
  const [detailedItems, setDetailedItems] = useState(initialKind === TRANSACTION_KINDS.DETAILED ? normalizeDetails(initialSource?.details) : []);
  const [confirmed, setConfirmedState] = useState(
    editData
      ? Boolean(editData?.confirmed ?? editData?.status)
      : isDateOnOrBeforeToday(initialSource?.date ? new Date(initialSource.date) : new Date())
  );
  const [confirmedManuallySet, setConfirmedManuallySet] = useState(false);
  const [reconciled, setReconciled] = useState(Boolean(editData?.reconciled));
  
  const [recurrenceType, setRecurrenceType] = useState("none"); // "none", "installment", "fixed"
  const [installmentCount, setInstallmentCount] = useState("2");
  const [recurrenceFrequency, setRecurrenceFrequency] = useState(DEFAULT_REPEAT_SETTINGS.recurrenceFrequency);
  const [recurrenceInterval, setRecurrenceInterval] = useState(DEFAULT_REPEAT_SETTINGS.recurrenceInterval);
  const [installmentStartIndex, setInstallmentStartIndex] = useState(DEFAULT_REPEAT_SETTINGS.installmentStartIndex);
  const [amountMode, setAmountMode] = useState(DEFAULT_REPEAT_SETTINGS.amountMode);
  const [fixedTotalEnabled, setFixedTotalEnabled] = useState(DEFAULT_REPEAT_SETTINGS.fixedTotalEnabled);
  const [fixedTotalCount, setFixedTotalCount] = useState(DEFAULT_REPEAT_SETTINGS.fixedTotalCount);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const transientAccountsRef = useRef([]);
  const submissionCategoryRef = useRef(null);

  const { addTransaction, updateTransaction, updateTransactionBatch, addTransactionBatch, addCategory, addAccount, categories, accounts, transactions } = useFinanceStore();

  const detailDescriptionSuggestions = useMemo(() => {
    const categoryType = categoryTypeForKind(transactionKind);
    const categoryNames = categories
      .filter((category) => category.type === categoryType)
      .map((category) => normalizeCategoryName(category.name));
    const detailNames = transactions.flatMap((transaction) => (
      Array.isArray(transaction.details)
        ? transaction.details.map((detail) => detail.description)
        : []
    ));

    return uniqueNames([...detailNames, ...categoryNames]).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [categories, transactions, transactionKind]);

  const form = useForm({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      description: getFormDescription(initialSource, initialKind),
      amount: initialSource?.amount?.toString() || "",
      category: normalizeCategoryName(initialSource?.category),
      account: initialAccountName,
      date: initialSource?.date ? new Date(initialSource.date) : new Date(),
      notes: initialSource?.notes || "",
    },
  });
  const selectedDate = form.watch("date");

  const setConfirmed = (value) => {
    setConfirmedManuallySet(true);
    setConfirmedState(Boolean(value));
  };

  const setTransactionKind = (kind) => {
    setTransactionKindState(kind);
    setType(categoryTypeForKind(kind));
    setCategoryIdValue(null);

    if (kind === TRANSACTION_KINDS.DETAILED) {
      setDetailedItems((current) => current.length ? current : [createDetailItem()]);
    } else if (kind === TRANSACTION_KINDS.TRANSFER) {
      if (!form.getValues("description")?.trim()) {
        form.setValue("description", "TRANSFERÊNCIA");
      }
    }
  };

  useEffect(() => {
    if (open) {
      const sourceData = editData || initialData || {};
      const nextKind = getTransactionKind(sourceData);
      const transferAccounts = nextKind === TRANSACTION_KINDS.TRANSFER
        ? getEditableTransferAccounts(transactions, sourceData, accounts)
        : { fromAccount: null, toAccount: null };
      const initialAccountName = initialData?.account || initialData?.from_account || "";
      const regularAccountName = editData
        ? nextKind === TRANSACTION_KINDS.TRANSFER
          ? transferAccounts.fromAccount
          : getAccountDisplayName(editData || {}, accounts)
        : nextKind === TRANSACTION_KINDS.TRANSFER
          ? transferAccounts.fromAccount
          : initialAccountName;
      const resolvedCategory = editData
        ? getTransactionCategory(editData, categories)
        : (
            findCategoryById(categories, initialData?.category_id, categoryTypeForKind(nextKind))
            || findUniqueCategoryByName(categories, normalizeCategoryName(initialData?.category), categoryTypeForKind(nextKind))
          );

      setTransactionKindState(nextKind);
      setType(categoryTypeForKind(nextKind));
      setCategoryValue(normalizeCategoryName(resolvedCategory?.name || sourceData?.category));
      setCategoryIdValue(resolvedCategory?.id || null);
      setAccountValue(regularAccountName === "—" ? "" : regularAccountName || "");
      setDestinationAccountValue(transferAccounts.toAccount || "");
      setDetailedItems(nextKind === TRANSACTION_KINDS.DETAILED ? normalizeDetails(sourceData?.details) : []);
      setConfirmedManuallySet(false);
      setConfirmedState(
        editData
          ? Boolean(editData?.confirmed ?? editData?.status)
          : isDateOnOrBeforeToday(sourceData?.date ? new Date(sourceData.date) : new Date())
      );
      setReconciled(Boolean(editData?.reconciled || (!editData && initialData?.reconciled)));

      if (editData?.recurrence_type) {
        setRecurrenceType(editData.recurrence_type);
      } else {
        setRecurrenceType("none");
      }
      setRecurrenceFrequency(editData?.recurrence_frequency || DEFAULT_REPEAT_SETTINGS.recurrenceFrequency);
      setRecurrenceInterval(String(editData?.recurrence_interval || DEFAULT_REPEAT_SETTINGS.recurrenceInterval));
      setInstallmentCount(String(editData?.total_installments || DEFAULT_REPEAT_SETTINGS.installmentCount));
      setInstallmentStartIndex(String(editData?.installment_start_index || editData?.installment_index || DEFAULT_REPEAT_SETTINGS.installmentStartIndex));
      setAmountMode(editData?.amount_mode || DEFAULT_REPEAT_SETTINGS.amountMode);
      setFixedTotalEnabled(Boolean(editData?.fixed_total_occurrences));
      setFixedTotalCount(String(editData?.fixed_total_occurrences || editData?.total_installments || DEFAULT_REPEAT_SETTINGS.fixedTotalCount));
      setPendingSubmitData(null);
      form.reset({
        description: getFormDescription(sourceData, nextKind),
        amount: sourceData?.amount?.toString() || "",
        category: normalizeCategoryName(resolvedCategory?.name || sourceData?.category),
        account: regularAccountName === "—" ? "" : regularAccountName || "",
        date: sourceData?.date ? new Date(sourceData.date) : new Date(),
        notes: sourceData?.notes || "",
      });
    }
  }, [open, editData, initialData, form, transactions, accounts, categories]);

  useEffect(() => {
    if (!open || editData || confirmedManuallySet) return;
    setConfirmedState(isDateOnOrBeforeToday(selectedDate));
  }, [open, editData, confirmedManuallySet, selectedDate]);

  const onSubmit = async (data) => {
    const normalizedData = {
      ...data,
      description: normalizeUppercase(data.description),
    };

    // Se está editando uma série, pausa para perguntar o escopo
    if (editData?.recurrence_id && !pendingSubmitData?.scopeConfirmed) {
      setPendingSubmitData({ data: normalizedData, scopeConfirmed: false });
      return;
    }
    await executeSubmission(normalizedData, pendingSubmitData?.scope || "current");
  };

  const confirmScope = async (scope) => {
    if (!pendingSubmitData) return;
    setPendingSubmitData({ ...pendingSubmitData, scopeConfirmed: true, scope });
    await executeSubmission(pendingSubmitData.data, scope);
  };

  const buildOccurrences = (data, totalAmount) => {
    const frequency = recurrenceFrequency || DEFAULT_REPEAT_SETTINGS.recurrenceFrequency;
    const interval = toPositiveInt(recurrenceInterval, 1);
    let count = 1;
    let totalInstallments = 1;
    let startIndex = 1;
    let amountPerOccurrence = totalAmount;

    if (recurrenceType === "installment") {
      totalInstallments = toPositiveInt(installmentCount, 2, 2);
      startIndex = Math.min(toPositiveInt(installmentStartIndex, 1), totalInstallments);
      count = totalInstallments - startIndex + 1;
      amountPerOccurrence = amountMode === "total" ? totalAmount / totalInstallments : totalAmount;
    } else if (recurrenceType === "fixed") {
      count = fixedTotalEnabled
        ? toPositiveInt(fixedTotalCount, 1)
        : calculateOccurrencesUntilDecember(data.date, frequency, interval);
      totalInstallments = count;
    }

    const recurrenceId = recurrenceType !== "none" ? crypto.randomUUID() : null;
    const baseDescription = normalizeUppercase(data.description);

    return Array.from({ length: count }, (_, index) => {
      const txDate = recurrenceType === "none"
        ? data.date
        : addByFrequency(data.date, frequency, index * interval);
      const installmentIndex = recurrenceType === "installment" ? startIndex + index : index + 1;
      const recurrenceData = recurrenceType === "none"
        ? {}
        : {
            recurrence_id: recurrenceId,
            recurrence_type: recurrenceType,
            recurrence_frequency: frequency,
            recurrence_interval: interval,
            installment_index: installmentIndex,
            total_installments: totalInstallments,
            amount_mode: recurrenceType === "installment" ? amountMode : "per_occurrence",
            installment_start_index: recurrenceType === "installment" ? startIndex : undefined,
            fixed_total_occurrences: recurrenceType === "fixed" && fixedTotalEnabled ? count : undefined,
          };

      return {
        date: txDate,
        amount: roundMoney(amountPerOccurrence),
        description: recurrenceType === "installment"
          ? `${baseDescription} (${installmentIndex}/${totalInstallments})`
          : baseDescription,
        recurrenceData,
        installmentIndex,
        totalInstallments,
      };
    });
  };

  const ensureAccount = async (name) => {
    const normalizedName = normalizeUppercase(name);
    if (!normalizedName) return null;

    const existing = [...accounts, ...transientAccountsRef.current]
      .find((account) => normalizeUppercase(account.name) === normalizedName);
    if (existing) return existing;

    const result = await addAccount({
        name: normalizedName,
        type: "checking",
        nature: "D",
        currency: "BRL",
        bankIconId: null,
        initialBalance: 0,
      });
    if (result?.account) {
      transientAccountsRef.current = [...transientAccountsRef.current, result.account];
    }
    return result?.account || null;
  };

  const findAccountByName = (name) => {
    const normalizedName = normalizeUppercase(name);
    return [...accounts, ...transientAccountsRef.current]
      .find((item) => normalizeUppercase(item.name) === normalizedName);
  };

  const executeSubmission = async (data, scope) => {
    transientAccountsRef.current = [];
    const categoryType = categoryTypeForKind(transactionKind);
    const filteredCategories = categories.filter((cat) => cat.type === categoryType);
    
    const normalizedCategoryValue = normalizeCategoryName(categoryValue);
    const categoryById = findCategoryById(categories, categoryIdValue, categoryType);
    let selectedCategory = categoryById || findUniqueCategoryByName(categories, normalizedCategoryValue, categoryType);

    if (!normalizedCategoryValue && transactionKind !== TRANSACTION_KINDS.TRANSFER) {
      toast.error("Categoria é obrigatória.");
      return;
    }

    if (normalizedCategoryValue && !selectedCategory && !filteredCategories.find(c => normalizeCategoryName(c.name) === normalizedCategoryValue)) {
      const result = await addCategory(categoryType, { name: normalizedCategoryValue, color: stringToColor(normalizedCategoryValue) });
      if (result?.success === false) {
        toast.error("Erro ao adicionar categoria");
        return;
      }
      selectedCategory = result.category || null;
      if (selectedCategory?.id) setCategoryIdValue(selectedCategory.id);
    }

    submissionCategoryRef.current = selectedCategory || null;
    
    if (transactionKind === TRANSACTION_KINDS.TRANSFER) {
      if (!normalizeUppercase(accountValue) || !normalizeUppercase(destinationAccountValue)) {
        toast.error("Informe a conta origem e a conta destino.");
        return;
      }
      if (normalizeUppercase(accountValue) === normalizeUppercase(destinationAccountValue)) {
        toast.error("Conta origem e destino precisam ser diferentes.");
        return;
      }
      const origin = await ensureAccount(accountValue);
      const destination = await ensureAccount(destinationAccountValue);
      if (!origin || !destination) {
        toast.error("Não foi possível preparar as contas da transferência.");
        return;
      }
    } else {
      const account = await ensureAccount(accountValue);
      if (accountValue && !account) {
        toast.error("Não foi possível preparar a conta.");
        return;
      }
    }
    
    const totalAmount = parseCurrency(data.amount);
    if (!totalAmount || totalAmount <= 0) {
      toast.error("Valor inválido.");
      return;
    }
    
    if (editData) {
      await handleUpdate(data, scope, totalAmount);
    } else {
      await handleCreate(data, totalAmount);
    }
  };

  const buildDetailedPayload = (targetAmount) => {
    const validation = validateDetailedItems(detailedItems, targetAmount);
    if (!validation.success) {
      toast.error(validation.message);
      return null;
    }

    return {
      transaction_kind: TRANSACTION_KINDS.DETAILED,
      details: validation.details,
      notes: form.getValues("notes") || "",
      status: confirmed,
      confirmed,
      reconciled,
    };
  };

  const getCategoryPayloadFields = () => {
    const category = submissionCategoryRef.current;
    const categoryName = normalizeCategoryName(category?.name || categoryValue) || null;
    return {
      category: categoryName,
      category_id: category?.id || null,
    };
  };

  const buildRegularPayload = ({ baseData, amount, txDate, description }) => {
    const cleanBase = stripKindData(baseData);
    const detailedPayload = transactionKind === TRANSACTION_KINDS.DETAILED
      ? buildDetailedPayload(amount)
      : null;

    if (transactionKind === TRANSACTION_KINDS.DETAILED && !detailedPayload) {
      return null;
    }

    const account = findAccountByName(accountValue);
    const categoryFields = getCategoryPayloadFields();

    return {
      ...cleanBase,
      description,
      amount: roundMoney(amount),
      ...categoryFields,
      account_id: account?.id || cleanBase.account_id || null,
      account: account?.name || normalizeUppercase(accountValue) || null,
      date: txDate.toISOString(),
      status: confirmed,
      confirmed,
      ...detailedPayload,
    };
  };

  const handleUpdate = async (data, scope, totalAmount) => {
    if (transactionKind === TRANSACTION_KINDS.TRANSFER) {
      await handleTransferUpdate(data, scope, totalAmount);
      return;
    }

    if (!editData.recurrence_id || scope === "current") {
      const baseData = recurrenceType === "none" ? stripRecurrenceData(editData) : editData;
      const transactionData = buildRegularPayload({
        baseData,
        amount: totalAmount,
        txDate: data.date,
        description: normalizeUppercase(data.description),
      });

      if (!transactionData) return;

      const result = await updateTransaction(editData.id, storageTypeForKind(transactionKind), transactionData);
      if (result.success) {
        toast.success("Transação atualizada!");
        closeDialog();
      } else toast.error("Erro ao atualizar");
    } else {
      const series = transactions.filter(t => t.recurrence_id === editData.recurrence_id);
      const toUpdate = scope === "all" ? series : series.filter(t => new Date(t.date) >= new Date(editData.date));
        
      const updates = toUpdate.map((t) => {
        let updatedDesc = normalizeUppercase(data.description);
        if (t.installment_index && t.total_installments && t.recurrence_type === "installment") {
          updatedDesc = `${normalizeUppercase(data.description)} (${t.installment_index}/${t.total_installments})`;
        }
        const baseData = recurrenceType === "none" ? stripRecurrenceData(t) : t;
        const payload = buildRegularPayload({
          baseData,
          amount: totalAmount,
          txDate: shiftDateByDelta(t.date, editData.date, data.date),
          description: updatedDesc,
        });

        return payload ? {
          id: t.id,
          type: storageTypeForKind(transactionKind),
          data: payload,
        } : null;
      }).filter(Boolean);

      if (!updates.length) return;
      
      const result = await updateTransactionBatch(updates);
      if (result.success) {
        toast.success(`${updates.length} transações atualizadas!`);
        closeDialog();
      } else toast.error("Erro ao atualizar lote");
    }
  };

  const getTransferUpdateGroups = (scope) => {
    let selected = [];

    if (!editData.recurrence_id || scope === "current") {
      selected = transactions.filter((tx) => (
        tx.linked_transfer_id && tx.linked_transfer_id === editData.linked_transfer_id
      ));
      if (!selected.find((tx) => tx.id === editData.id)) {
        selected.push(editData);
      }
    } else {
      const series = transactions.filter(t => t.recurrence_id === editData.recurrence_id);
      selected = scope === "all" ? series : series.filter(t => new Date(t.date) >= new Date(editData.date));
    }

    const groups = new Map();
    selected.forEach((tx) => {
      const key = tx.linked_transfer_id || tx.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(tx);
    });

    return Array.from(groups.entries()).map(([linkedId, pair]) => ({ linkedId, pair }));
  };

  const buildTransferPayload = ({ transaction, role, linkedId, data, amount, date }) => {
    const baseData = recurrenceType === "none" ? stripRecurrenceData(transaction) : transaction;
    const cleanBase = stripKindData(baseData);
    const originAccount = normalizeUppercase(accountValue);
    const targetAccount = normalizeUppercase(destinationAccountValue);
    const origin = findAccountByName(originAccount);
    const target = findAccountByName(targetAccount);
    const categoryFields = getCategoryPayloadFields();
    const baseDescription = normalizeUppercase(data.description) || "TRANSFERÊNCIA";
    const sideDescription = role === "origin"
      ? `${baseDescription} PARA ${targetAccount}`
      : `${baseDescription} DE ${originAccount}`;
    const description = appendInstallmentSuffix(sideDescription, transaction);

    return {
      ...cleanBase,
      description,
      amount: roundMoney(amount),
      ...categoryFields,
      account_id: role === "origin" ? origin?.id || null : target?.id || null,
      account: role === "origin" ? originAccount : targetAccount,
      date: date.toISOString(),
      status: confirmed,
      confirmed,
      transaction_kind: TRANSACTION_KINDS.TRANSFER,
      linked_transfer_id: linkedId,
      transfer_role: role,
      from_account: originAccount,
      to_account: targetAccount,
      from_account_id: origin?.id || null,
      to_account_id: target?.id || null,
      transfer_description: baseDescription,
    };
  };

  const handleTransferUpdate = async (data, scope, totalAmount) => {
    const groups = getTransferUpdateGroups(scope);
    const updates = groups.flatMap(({ linkedId, pair }) => {
      const origin = pair.find((tx) => tx.type === "expense") || pair[0];
      const destination = pair.find((tx) => tx.type === "income");
      const date = (!editData.recurrence_id || scope === "current")
        ? data.date
        : shiftDateByDelta(origin?.date || destination?.date || data.date, editData.date, data.date);

      return [
        origin ? {
          id: origin.id,
          type: "expense",
          data: buildTransferPayload({
            transaction: origin,
            role: "origin",
            linkedId,
            data,
            amount: totalAmount,
            date,
          }),
        } : null,
        destination ? {
          id: destination.id,
          type: "income",
          data: buildTransferPayload({
            transaction: destination,
            role: "destination",
            linkedId,
            data,
            amount: totalAmount,
            date,
          }),
        } : null,
      ].filter(Boolean);
    });

    if (!updates.length) {
      toast.error("Não foi possível localizar o par da transferência.");
      return;
    }

    const result = await updateTransactionBatch(updates);
    if (result.success) {
      toast.success(`${updates.length} lançamento${updates.length > 1 ? "s" : ""} atualizado${updates.length > 1 ? "s" : ""}!`);
      closeDialog();
    } else {
      toast.error("Erro ao atualizar transferência");
    }
  };

  const createTransferTransactions = async (data, occurrences) => {
    const originAccount = normalizeUppercase(accountValue);
    const targetAccount = normalizeUppercase(destinationAccountValue);
    const context = {
      baseDescription: normalizeUppercase(data.description),
      originAccount,
      targetAccount,
      origin: findAccountByName(originAccount),
      target: findAccountByName(targetAccount),
      categoryFields: getCategoryPayloadFields(),
      confirmed,
    };
    const transactionsToCreate = occurrences.flatMap((occurrence) =>
      buildTransferOccurrenceTransactions(occurrence, context)
    );

    const result = await addTransactionBatch(transactionsToCreate);
    if (result.success) {
      toast.success(`${occurrences.length} transferência${occurrences.length > 1 ? "s" : ""} criada${occurrences.length > 1 ? "s" : ""}!`);
      closeDialog();
    } else {
      toast.error("Erro ao adicionar transferência");
    }
  };

  const createStandardTransactions = async (occurrences, firstOccurrenceAmount) => {
    const detailedPayload = transactionKind === TRANSACTION_KINDS.DETAILED
      ? buildDetailedPayload(firstOccurrenceAmount)
      : null;

    if (transactionKind === TRANSACTION_KINDS.DETAILED && !detailedPayload) {
      return;
    }

    const transactionsToCreate = occurrences.map((occurrence) => ({
      type: storageTypeForKind(transactionKind),
      data: {
        description: occurrence.description,
        amount: occurrence.amount,
        ...getCategoryPayloadFields(),
        account_id: findAccountByName(accountValue)?.id || null,
        account: normalizeUppercase(accountValue) || null,
        date: occurrence.date.toISOString(),
        status: confirmed,
        confirmed,
        ...(transactionKind === TRANSACTION_KINDS.DETAILED ? detailedPayload : {}),
        ...occurrence.recurrenceData,
      }
    }));

    if (transactionsToCreate.length === 1) {
      const result = await addTransaction(storageTypeForKind(transactionKind), transactionsToCreate[0].data);
      if (result.success) {
        toast.success("Transação adicionada!");
        closeDialog();
      } else toast.error("Erro ao adicionar transação");
    } else {
      const result = await addTransactionBatch(transactionsToCreate);
      if (result.success) {
        toast.success(`${occurrences.length} transações criadas com sucesso!`);
        closeDialog();
      } else toast.error("Erro ao adicionar lote de transações");
    }
  };

  const handleCreate = async (data, totalAmount) => {
    const occurrences = buildOccurrences(data, totalAmount);
    const firstOccurrenceAmount = occurrences[0]?.amount || totalAmount;

    if (transactionKind === TRANSACTION_KINDS.DETAILED) {
      const validation = validateDetailedItems(detailedItems, firstOccurrenceAmount);
      if (!validation.success) {
        toast.error(validation.message);
        return;
      }
    }

    if (transactionKind === TRANSACTION_KINDS.TRANSFER) {
      await createTransferTransactions(data, occurrences);
      return;
    }

    await createStandardTransactions(occurrences, firstOccurrenceAmount);
  };

  const createChildCategory = async (parentCategory, name, options = {}) => {
    const categoryType = categoryTypeForKind(transactionKind);
    const normalizedName = normalizeCategoryName(name);
    const shouldSelectCategory = options.selectCategory !== false;

    if (!parentCategory?.id || parentCategory.type !== categoryType) {
      toast.error("Categoria pai inválida.");
      return { success: false };
    }
    if (!normalizedName) {
      toast.error("Nome da categoria é obrigatório.");
      return { success: false };
    }
    if (categories.some((category) => (
      category.type === categoryType &&
      (category.parent_id || null) === parentCategory.id &&
      normalizeCategoryName(category.name) === normalizedName
    ))) {
      toast.error("Já existe uma categoria com esse nome neste pai.");
      return { success: false };
    }

    const validation = validateCategoryParent(categories, null, parentCategory.id, categoryType);
    if (!validation.valid) {
      toast.error(validation.message);
      return { success: false };
    }

    const result = await addCategory(categoryType, {
      name: normalizedName,
      color: parentCategory.color || stringToColor(normalizedName),
      parent_id: parentCategory.id,
    });

    if (result.success) {
      if (shouldSelectCategory) {
        setCategoryValue(normalizedName);
        setCategoryIdValue(result.category?.id || null);
      }
      toast.success("Categoria filha adicionada!");
    }

    return result;
  };

  const closeDialog = () => {
    form.reset();
    setTransactionKindState(TRANSACTION_KINDS.EXPENSE);
    setType(categoryTypeForKind(TRANSACTION_KINDS.EXPENSE));
    setCategoryValue("");
    setCategoryIdValue(null);
    setAccountValue("");
    setDestinationAccountValue("");
    setDetailedItems([]);
    setConfirmedState(false);
    setConfirmedManuallySet(false);
    setReconciled(false);
    setRecurrenceType("none");
    setRecurrenceFrequency(DEFAULT_REPEAT_SETTINGS.recurrenceFrequency);
    setRecurrenceInterval(DEFAULT_REPEAT_SETTINGS.recurrenceInterval);
    setInstallmentCount(DEFAULT_REPEAT_SETTINGS.installmentCount);
    setInstallmentStartIndex(DEFAULT_REPEAT_SETTINGS.installmentStartIndex);
    setAmountMode(DEFAULT_REPEAT_SETTINGS.amountMode);
    setFixedTotalEnabled(DEFAULT_REPEAT_SETTINGS.fixedTotalEnabled);
    setFixedTotalCount(DEFAULT_REPEAT_SETTINGS.fixedTotalCount);
    setPendingSubmitData(null);
    submissionCategoryRef.current = null;
    onOpenChange(false);
  };

  return {
    form,
    type,
    setType,
    transactionKind,
    setTransactionKind,
    categoryValue,
    setCategoryValue,
    categoryIdValue,
    setCategoryIdValue,
    accountValue,
    setAccountValue,
    destinationAccountValue,
    setDestinationAccountValue,
    detailedItems,
    setDetailedItems,
    detailDescriptionSuggestions,
    confirmed,
    setConfirmed,
    reconciled,
    setReconciled,
    recurrenceType,
    setRecurrenceType,
    recurrenceFrequency,
    setRecurrenceFrequency,
    recurrenceInterval,
    setRecurrenceInterval,
    installmentCount,
    setInstallmentCount,
    installmentStartIndex,
    setInstallmentStartIndex,
    amountMode,
    setAmountMode,
    fixedTotalEnabled,
    setFixedTotalEnabled,
    fixedTotalCount,
    setFixedTotalCount,
    onSubmit,
    categories,
    accounts,
    pendingSubmitData,
    confirmScope,
    createChildCategory,
    closeDialog,
  };
};
